import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Facebook, Twitter, Linkedin, Youtube, Instagram } from "lucide-react";

const SOCIAL_ICONS = { facebook: Facebook, twitter: Twitter, linkedin: Linkedin, youtube: Youtube, instagram: Instagram };

export default function Footer({ settings }) {
  const s = settings;
  const socials = Object.entries(s?.socials || {}).filter(([, url]) => !!url);
  const footerMark = s?.footer_mark || "/logos/aoa_mark_white_v3.png";

  const openCookieBanner = () => window.dispatchEvent(new Event("reopen-cookie-banner"));

  return (
    <footer className="relative bg-[hsl(var(--aoa-navy))] text-slate-200" data-testid="site-footer">
      <div className="absolute inset-0 aoa-grain opacity-50 pointer-events-none" aria-hidden />
      <div className="aoa-container relative pt-20 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start">
          <div className="md:col-span-4 space-y-4">
            <Link to="/" className="inline-flex flex-col items-start gap-0 group" data-testid="footer-logo-link">
              <img
                src={footerMark}
                alt="AOA Somalia logo"
                width={180}
                height={112}
                loading="lazy"
                decoding="async"
                className="block h-24 md:h-28 w-auto opacity-70 drop-shadow-[0_4px_20px_rgba(255,255,255,0.10)] transition-opacity duration-300 hover:opacity-100 -mt-2"
                data-testid="footer-logo-mark"
              />
              <div className="mt-4">
                <p className="text-white font-extrabold text-xl tracking-tight">AOA Somalia</p>
                <p className="text-white/60 text-sm font-medium tracking-wide">
                  Airline Operators Association of Somalia
                </p>
              </div>
            </Link>
            <p className="mt-5 text-sm leading-relaxed text-slate-300/90 max-w-xs">
              The collective voice of airline operators and aviation stakeholders in Somalia — supporting safer, more efficient, more connected air transport.
            </p>
            {socials.length > 0 && (
              <div className="mt-5 flex gap-3" data-testid="footer-socials">
                {socials.map(([k, url]) => {
                  const Ic = SOCIAL_ICONS[k];
                  if (!Ic) return null;
                  return (
                    <a key={k} href={url} target="_blank" rel="noreferrer" className="grid h-9 w-9 place-items-center rounded-full border border-white/20 text-slate-200 hover:bg-white hover:text-[hsl(var(--aoa-navy))] transition-colors" aria-label={k}>
                      <Ic className="h-4 w-4" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          <div className="md:col-span-2">
            <h4 className="font-display text-sm font-semibold uppercase tracking-[0.18em] text-white/80">About</h4>
            <ul className="mt-5 space-y-3 text-sm">
              {[["About AOA","/about"],["Vision & Mission","/vision-mission"],["What We Do","/what-we-do"],["Objectives","/objectives"],["Governance","/governance"],["Mandate","/mandate"]].map(([l,h]) => (
                <li key={h}><Link to={h} className="text-slate-300 hover:text-white transition-colors">{l}</Link></li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-display text-sm font-semibold uppercase tracking-[0.18em] text-white/80">Quick Links</h4>
            <ul className="mt-5 space-y-3 text-sm">
              {[["Membership","/membership"],["Committees","/committees"],["Stakeholder Engagement","/stakeholder-engagement"],["Resources","/resources"],["News & Updates","/news"],["Contact","/contact"]].map(([l,h]) => (
                <li key={h}><Link to={h} className="text-slate-300 hover:text-white transition-colors">{l}</Link></li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-display text-sm font-semibold uppercase tracking-[0.18em] text-white/80">Headquarters</h4>
            <ul className="mt-5 space-y-4 text-sm">
              <li className="flex gap-3"><MapPin className="h-4 w-4 mt-0.5 flex-none text-[hsl(var(--aoa-sky-light))]" /><span className="text-slate-300">{s?.contact?.address || "Mogadishu, Somalia"}</span></li>
              <li className="flex gap-3"><Mail className="h-4 w-4 mt-0.5 flex-none text-[hsl(var(--aoa-sky-light))]" /><a href={`mailto:${s?.contact?.email || "info@aoa.so"}`} className="text-slate-300 hover:text-white">{s?.contact?.email || "info@aoa.so"}</a></li>
              <li className="flex gap-3">
                <Phone className="h-4 w-4 mt-0.5 flex-none text-[hsl(var(--aoa-sky-light))]" />
                <div className="space-y-1">{(s?.contact?.phones || []).map((p) => <a key={p} href={`tel:${p.replace(/\s/g,"")}`} className="block text-slate-300 hover:text-white">{p}</a>)}</div>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 border-t border-white/10 pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <p className="text-xs text-slate-400">© 2026 Airline Operators Association of Somalia. All rights reserved.</p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
            <Link to="/privacy" className="text-slate-400 hover:text-white">Privacy Policy</Link>
            <span className="text-slate-600">·</span>
            <Link to="/terms" className="text-slate-400 hover:text-white">Terms of Use</Link>
            <span className="text-slate-600">·</span>
            <button onClick={openCookieBanner} data-testid="footer-cookie-preferences" className="text-slate-400 hover:text-white">Cookie Preferences</button>
          </div>
        </div>
      </div>
    </footer>
  );
}
